import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// Unified Nexus Store cart.
//
// The marketplace aggregates products from many tenants. A shopper can mix
// products from several merchants in ONE cart; at checkout we split the cart
// into a separate order per merchant (each merchant's order is processed in
// that merchant's own tenant schema). The cart itself lives purely client-side
// in localStorage — the public marketplace pages never touch a tenant schema,
// so there is no server-side cart here. State rehydrates from localStorage on
// every page load (some nexus-store navigations are full reloads).
// ─────────────────────────────────────────────────────────────────────────────

export type NexusCartItem = {
  tenantId: number;
  productId: number;
  slug: string;
  name: string;
  nameAr: string | null;
  price: number;
  salePrice: number | null;
  currency: string;
  mainImage: string | null;
  tenantSlug: string;
  // tenantName is kept for internal grouping only — it is intentionally NOT
  // surfaced at checkout, where merchants stay anonymous (logo only).
  tenantName: string;
  tenantLogoUrl?: string | null;
  qty: number;
};

export type NexusMerchantGroup = {
  tenantId: number;
  tenantSlug: string;
  tenantName: string;
  tenantLogoUrl: string | null;
  items: NexusCartItem[];
  subtotal: number;
  count: number;
};

type AddInput = Omit<NexusCartItem, "qty">;

type NexusCartContextValue = {
  items: NexusCartItem[];
  count: number;
  subtotal: number;
  groups: NexusMerchantGroup[];
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (item: AddInput, qty?: number) => void;
  setQty: (tenantId: number, productId: number, qty: number) => void;
  removeItem: (tenantId: number, productId: number) => void;
  // Drop every item belonging to the given merchants in one shot. Used by the
  // grouped checkout so only the merchants whose order succeeded are cleared,
  // while failed merchants stay in the cart for a retry.
  removeTenants: (tenantIds: number[]) => void;
  clear: () => void;
};

const STORAGE_KEY = "nexus_store_cart_v1";

const NexusCartContext = createContext<NexusCartContextValue | null>(null);

function readStorage(): NexusCartItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x) => x && typeof x.tenantId === "number" && typeof x.productId === "number" && typeof x.qty === "number",
    );
  } catch {
    return [];
  }
}

export function NexusCartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<NexusCartItem[]>(() => readStorage());
  const [isOpen, setIsOpen] = useState(false);

  // Persist on every change.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* quota / private mode — ignore */
    }
  }, [items]);

  // Keep multiple tabs in sync.
  useEffect(() => {
    if (typeof window === "undefined") return;
    function onStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY) setItems(readStorage());
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const addItem = useCallback((item: AddInput, qty = 1) => {
    setItems((prev) => {
      const i = prev.findIndex((p) => p.tenantId === item.tenantId && p.productId === item.productId);
      if (i >= 0) {
        const next = [...prev];
        next[i] = { ...next[i], qty: Math.max(1, next[i].qty + qty) };
        return next;
      }
      return [...prev, { ...item, qty: Math.max(1, qty) }];
    });
    setIsOpen(true);
  }, []);

  const setQty = useCallback((tenantId: number, productId: number, qty: number) => {
    setItems((prev) => {
      if (qty <= 0) return prev.filter((p) => !(p.tenantId === tenantId && p.productId === productId));
      return prev.map((p) => (p.tenantId === tenantId && p.productId === productId ? { ...p, qty } : p));
    });
  }, []);

  const removeItem = useCallback((tenantId: number, productId: number) => {
    setItems((prev) => prev.filter((p) => !(p.tenantId === tenantId && p.productId === productId)));
  }, []);

  const removeTenants = useCallback((tenantIds: number[]) => {
    const drop = new Set(tenantIds);
    setItems((prev) => prev.filter((p) => !drop.has(p.tenantId)));
  }, []);

  const clear = useCallback(() => setItems([]), []);
  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const value = useMemo<NexusCartContextValue>(() => {
    const count = items.reduce((s, p) => s + p.qty, 0);
    const subtotal = items.reduce((s, p) => s + (p.salePrice ?? p.price) * p.qty, 0);

    const byTenant = new Map<number, NexusMerchantGroup>();
    for (const it of items) {
      let g = byTenant.get(it.tenantId);
      if (!g) {
        g = {
          tenantId: it.tenantId,
          tenantSlug: it.tenantSlug,
          tenantName: it.tenantName,
          tenantLogoUrl: it.tenantLogoUrl ?? null,
          items: [],
          subtotal: 0,
          count: 0,
        };
        byTenant.set(it.tenantId, g);
      }
      g.items.push(it);
      g.subtotal += (it.salePrice ?? it.price) * it.qty;
      g.count += it.qty;
      if (!g.tenantLogoUrl && it.tenantLogoUrl) g.tenantLogoUrl = it.tenantLogoUrl;
    }

    return {
      items,
      count,
      subtotal,
      groups: Array.from(byTenant.values()),
      isOpen,
      openCart,
      closeCart,
      addItem,
      setQty,
      removeItem,
      removeTenants,
      clear,
    };
  }, [items, isOpen, openCart, closeCart, addItem, setQty, removeItem, removeTenants, clear]);

  return <NexusCartContext.Provider value={value}>{children}</NexusCartContext.Provider>;
}

export function useNexusCart(): NexusCartContextValue {
  const ctx = useContext(NexusCartContext);
  if (!ctx) throw new Error("useNexusCart must be used within a NexusCartProvider");
  return ctx;
}
